import React, { useState, useRef, useEffect } from 'react';
import { FaPaperPlane, FaLightbulb, FaSpinner } from 'react-icons/fa';
import { API_BASE_URL } from '../config/api';

interface Message {
  sender: 'user' | 'bot';
  text: string;
  suggestions?: string[];
}

const AiBotChat: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    { 
      sender: 'bot', 
      text: 'سلام! I am your Farsi learning assistant. How can I help you today? (Hello! I am your Farsi learning assistant. How can I help you today?)',
      suggestions: ['گرامر فارسی', 'لغات جدید', 'تلفظ', 'فرهنگ ایرانی']
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (messageText?: string) => {
    const textToSend = messageText || input.trim();
    if (!textToSend) return;

    const detectedLanguage = textToSend.match(/[\u0600-\u06FF]/) ? 'persian' : 'english';
    const userMessage: Message = { sender: 'user', text: textToSend };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/ai-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: textToSend,
          language: detectedLanguage
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error getting AI response:', response.status, errorText);
        throw new Error(`Failed to get AI response: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        const botMessage: Message = {
          sender: 'bot',
          text: data.response,
          suggestions: data.suggestions
        };
        setMessages((prev) => [...prev, botMessage]);
      } else {
        throw new Error(data.message || 'Failed to get response');
      }
    } catch (error) {
      console.error('Error getting AI response:', error);
      const errorMessage: Message = {
        sender: 'bot',
        text: 'متأسفم، مشکلی پیش آمد. لطفاً دوباره تلاش کنید. (Sorry, something went wrong. Please try again.)'
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isLoading) handleSend();
  };

  const handleSuggestionClick = (suggestion: string) => {
    handleSend(suggestion);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col items-center justify-center py-12">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-lg p-6 flex flex-col" style={{ height: 600 }}>
        <div className="flex items-center justify-center mb-4">
          <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center mr-3">
            <FaLightbulb className="text-white text-lg" />
          </div>
          <h2 className="text-2xl font-bold text-indigo-700">Farsi Learning AI Assistant</h2>
        </div>
        
        <div className="flex-1 overflow-y-auto mb-4 space-y-4">
          {messages.map((msg, idx) => (
            <div key={idx}>
              <div
                className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`px-4 py-3 rounded-2xl max-w-xs lg:max-w-md break-words ${
                    msg.sender === 'user'
                      ? 'bg-indigo-600 text-white self-end'
                      : 'bg-gray-100 text-gray-800 self-start border border-gray-200'
                  }`}
                >
                  <div className="whitespace-pre-wrap">{msg.text}</div>
                </div>
              </div>
              
              {/* Suggestions */}
              {msg.sender === 'bot' && msg.suggestions && msg.suggestions.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {msg.suggestions.map((suggestion, suggestionIdx) => (
                    <button
                      key={suggestionIdx}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm hover:bg-indigo-200 transition-colors border border-indigo-200"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          
          {/* Loading indicator */}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 text-gray-800 px-4 py-3 rounded-2xl border border-gray-200">
                <div className="flex items-center space-x-2">
                  <FaSpinner className="animate-spin text-indigo-600" />
                  <span>در حال فکر کردن... (Thinking...)</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        <div className="flex gap-2">
          <input
            type="text"
            className="flex-1 px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
            placeholder="سوال خود را بپرسید... (Ask your question...)"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
          />
          <button
            className={`px-6 py-3 rounded-xl transition-colors flex items-center space-x-2 ${
              isLoading 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-indigo-600 hover:bg-indigo-700'
            } text-white`}
            onClick={() => handleSend()}
            disabled={isLoading}
          >
            <FaPaperPlane className="text-sm" />
            <span>Send</span>
          </button>
        </div>
        
        <div className="mt-4 text-center text-sm text-gray-500">
          <p>💡 Ask me about Persian grammar, vocabulary, pronunciation, culture, or anything else!</p>
        </div>
      </div>
    </div>
  );
};

export default AiBotChat; 